import apiInstance from '@services/api/apiInstance'
import { atom } from 'jotai'
import { uploadProgressStore } from '@services/uploadProgress/uploadProgress.store'

// Atom for storing technician information
export const technicianAtom = atom<number | null>(null)

export interface Technician {
  id: number
  fullName: string
  user: {
    id: number
    firstName: string
    lastName: string
    email: string
    fieldTechnician: number
  }
}

export interface CustomerAvailability {
  id: number
  availableFrom: string
  availableTo: string
}

export interface Ticket {
  id: number
  topic: string
  description: string
  status: string
  dateRepair: string | null
  investmentName: string
  subjectName: string
  faultCategoryName: string
  createdAt: string
  updatedAt: string
  customerAvailabilities?: CustomerAvailability[]
}

export interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

export interface TicketResponse extends PaginatedResponse<Ticket> {}

export interface RepairCost {
  id: number
  amount: string
  comment: string
  quantity: number
  responsibleSide: string
  repairType: {
    id: number
    name: string
  }
  acceptDate: string
  done: boolean
  technician: Technician | null
  file?: string
}

export interface RepairCostResponse extends PaginatedResponse<RepairCost> {}

export interface Photo {
  id: number
  file: string
  name: string
  comment: string
}

export interface TicketInternalComment {
  id: number
  comment: string
  author: {
    id: number
    firstName: string
    lastName: string
    email: string
  } | null
  createdOn: string
  photos: Photo[]
}

export interface InternalCommentResponse extends PaginatedResponse<TicketInternalComment> {}

export interface TicketMessage {
  id: number
  message: string
  attachment: string | null
  author: {
    id: number
    firstName: string
    lastName: string
    email: string
  } | null
  createdAt: string
  updatedAt: string
}

export interface MessageResponse extends PaginatedResponse<TicketMessage> {}

export interface PhotoUpload {
  file: File
  comment?: string
}

export interface TicketFilters {
  status?: string
  isInProgress?: boolean
  ordering?: string
  page?: number
  pageSize?: number
}

const helpdeskService = {
  // Fetching list of tickets assigned to technician
  getTickets: (filters?: TicketFilters) => {
    const params = new URLSearchParams()

    if (filters) {
      if (filters.status) {
        params.append('status', filters.status)
      }
      if (filters.isInProgress !== undefined) {
        params.append('is_in_progress', filters.isInProgress.toString())
      }
      if (filters.ordering) {
        params.append('ordering', filters.ordering)
      }
      if (filters.page) {
        params.append('page', filters.page.toString())
      }
      if (filters.pageSize) {
        params.append('page_size', filters.pageSize.toString())
      }
    }

    const queryString = params.toString()
    const url = queryString ? `/admin/helpdesk/tickets/?${queryString}` : '/admin/helpdesk/tickets/'

    return apiInstance.get<TicketResponse>(url)
  },

  // Fetching ticket details
  getTicket: (id: number) => {
    return apiInstance.get<Ticket>(`/admin/helpdesk/tickets/${id}/`)
  },

  // Fetching repair costs for a ticket
  getRepairCosts: (ticketId: number) => {
    return apiInstance.get<RepairCostResponse>(`/admin/helpdesk/tickets/${ticketId}/repair-costs/`)
  },

  // Assigning repair cost to technician
  assignRepairCost: (ticketId: number, repairCostId: number) => {
    return apiInstance.post<RepairCost>(
      `/admin/helpdesk/tickets/${ticketId}/repair-costs/${repairCostId}/assign/`,
      {}
    )
  },

  // Marking repair cost as done
  markRepairCostAsDone: (ticketId: number, repairCostId: number) => {
    return apiInstance.post<RepairCost>(
      `/admin/helpdesk/tickets/${ticketId}/repair-costs/${repairCostId}/done/`,
      {}
    )
  },

  // Fetching internal comments for a ticket
  getInternalComments: (ticketId: number) => {
    return apiInstance.get<InternalCommentResponse>(`/admin/helpdesk/tickets/${ticketId}/comments/`)
  },

  // Adding internal comment to a ticket
  addInternalComment: async (ticketId: number, comment: string, photoUploads?: PhotoUpload[]) => {
    if (!comment.trim() && (!photoUploads || photoUploads.length === 0)) {
      console.error('addInternalComment: Empty comment and no photos')
      throw new Error('Comment cannot be empty when there are no attachments')
    }

    const formData = new FormData()
    formData.append('comment', comment)

    if (photoUploads && photoUploads.length > 0) {
      // Important: We use exactly the same field name as in curl - 'photos' instead of 'photo_uploads'
      photoUploads.forEach((photo, index) => {
        formData.append(`photos`, photo.file)
        if (photo.comment) {
          formData.append(`photo_comments[${index}]`, photo.comment)
        }
      })
    }

    try {
      // We use exactly the same URL as in curl
      const url = `/admin/helpdesk/tickets/${ticketId}/comments/`
      const hasFiles = Boolean(photoUploads && photoUploads.length > 0)
      const estimatedTotal = hasFiles
        ? photoUploads!.reduce((sum, photo) => sum + photo.file.size, 0)
        : 0

      if (hasFiles) {
        uploadProgressStore.setMeta({
          fileName:
            photoUploads!.length === 1
              ? photoUploads![0].file.name
              : `${photoUploads!.length} zdjęcia`,
          fileIndex: 1,
          totalFiles: 1,
          estimatedTotal
        })
      }

      try {
        const response = await apiInstance.post<TicketInternalComment>(url, formData, {
          transformRequest: [(data) => data], // Prevent FormData transformation
          ...(hasFiles && {
            onUploadProgress: (event) =>
              uploadProgressStore.handleAxiosProgress(event, estimatedTotal)
          })
        })

        if (hasFiles) {
          uploadProgressStore.setProcessing()
        }

        return response
      } catch (error) {
        if (hasFiles) {
          uploadProgressStore.clear()
        }
        throw error
      }
    } catch (error) {
      console.error('Error adding comment:', error)
      throw error
    }
  },

  // Fetching messages for a ticket
  getMessages: (ticketId: number) => {
    return apiInstance.get<MessageResponse>(`/admin/helpdesk/tickets/${ticketId}/messages/`)
  },

  // Adding message to a ticket
  addMessage: async (ticketId: number, message: string, attachment?: File) => {
    if (!message.trim()) {
      console.error('addMessage: Empty message')
      throw new Error('Message cannot be empty')
    }

    const formData = new FormData()

    // Important: We use exactly the same field name as in curl
    formData.append('message', message)

    if (attachment) {
      // Important: We use exactly the same field name as in curl
      formData.append('attachment', attachment)
    }

    try {
      // We use exactly the same URL as in curl
      const url = `/admin/helpdesk/tickets/${ticketId}/messages/`
      const hasFile = Boolean(attachment)
      const estimatedTotal = attachment?.size ?? 0

      if (hasFile && attachment) {
        uploadProgressStore.setMeta({
          fileName: attachment.name,
          fileIndex: 1,
          totalFiles: 1,
          estimatedTotal
        })
      }

      try {
        const response = await apiInstance.post<TicketMessage>(url, formData, {
          transformRequest: [(data) => data], // Prevent FormData transformation
          ...(hasFile && {
            onUploadProgress: (event) =>
              uploadProgressStore.handleAxiosProgress(event, estimatedTotal)
          })
        })

        if (hasFile) {
          uploadProgressStore.setProcessing()
        }

        return response
      } catch (error) {
        if (hasFile) {
          uploadProgressStore.clear()
        }
        throw error
      }
    } catch (error) {
      console.error('Error adding message:', error)
      throw error
    }
  },

  // Getting technician information
  getTechnician: () => {
    return apiInstance.get<Technician>('/admin/helpdesk/technician/')
  },

  // Updating ticket status
  updateTicketStatus: (ticketId: number, status: string) => {
    return apiInstance.patch<Ticket>(`/admin/helpdesk/tickets/${ticketId}/`, { status })
  }
}

export default helpdeskService
